import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import nurseryQuarantineService from '../../services/nurseryQuarantine';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type { QuarantineQueueLoad, QuarantineQueueOrder } from '../../types/nurseryQuarantine';

/**
 * On /nursery/quarantine: every open load's orders still to be treated before
 * they can go (Rob, 2026-10-15). Picked orders are ready for the spray area
 * and come first; those still being picked follow, so the spraying can be
 * planned. An order whose state cannot be read holds the load too, so it is
 * listed with that reason. The treatment itself is made on the load's
 * Quarantine card.
 */

const GAP_TEXT: Record<QuarantineQueueOrder['gap'], string> = {
  untreated: 'No treatment yet',
  waiting: 'Treatment to spray or validate',
  expired: 'Treatment past its window: treat again',
  no_state: 'No state on its address: add one to see which protocol applies',
};

const GAP_CLS: Record<QuarantineQueueOrder['gap'], string> = {
  untreated: 'bg-red-50 text-red-700 border-red-200',
  waiting: 'bg-amber-50 text-amber-800 border-amber-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
  no_state: 'bg-red-50 text-red-700 border-red-200',
};

function OrderRow({ o }: { o: QuarantineQueueOrder }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm">
      <span className="font-medium text-neutral-800">{o.order_number}</span>
      <span className="text-neutral-600 min-w-0 break-words">{[o.customer_name, o.state].filter(Boolean).join(', ')}</span>
      {o.codes.length > 0 && <span className="text-xs text-neutral-500">{o.codes.join(', ')}</span>}
      <span className={`text-xs px-2 py-0.5 rounded-full border ${GAP_CLS[o.gap]}`}>{GAP_TEXT[o.gap]}</span>
    </li>
  );
}

function LoadBlock({ l, tz }: { l: QuarantineQueueLoad; tz: string }) {
  const picked = l.orders.filter((o) => o.picked);
  const picking = l.orders.filter((o) => !o.picked);
  return (
    <div className="border border-neutral-200 rounded-lg p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link to={`/nursery/loads/${l.load_id}`} className="text-sm font-semibold text-primary-700 hover:underline break-words">
          {`Load ${l.load_number}`}
        </Link>
        <span className="text-xs text-neutral-500 break-words">
          {[l.despatch_date ? `Despatch ${formatLocalDateInTz(l.despatch_date, tz)}` : 'No despatch date', l.despatch_farm_name].filter(Boolean).join(' · ')}
        </span>
      </div>
      {picked.length > 0 && (
        <>
          <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mt-2">Picked: take to the spray area</p>
          <ul className="divide-y divide-neutral-100">{picked.map((o) => <OrderRow key={o.order_id} o={o} />)}</ul>
        </>
      )}
      {picking.length > 0 && (
        <>
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mt-2">Still being picked</p>
          <ul className="divide-y divide-neutral-100 opacity-80">{picking.map((o) => <OrderRow key={o.order_id} o={o} />)}</ul>
        </>
      )}
    </div>
  );
}

export function QuarantineQueue() {
  const tz = useOrgTimezone();
  const [loads, setLoads] = useState<QuarantineQueueLoad[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryQuarantineService.getQueue()
      .then((res) => {
        if (res.success) setLoads(res.data ?? []);
        else setError(errorMessage(res, 'Could not load the orders to treat'));
      })
      .catch((e) => setError(errorMessage(e, 'Could not load the orders to treat')));
  }, []);

  const pickedCount = (loads ?? []).reduce((n, l) => n + l.orders.filter((o) => o.picked).length, 0);
  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <div>
        <h2 className="text-base font-semibold text-neutral-800">To treat before despatch</h2>
        <p className="text-xs text-neutral-500">
          Orders on open loads going under a protocol that still need a validated treatment. Make the treatment on the load's Quarantine card.
        </p>
      </div>
      {error ? (
        <p className="text-sm text-red-700">{error}</p>
      ) : loads == null ? (
        <div className="h-12 bg-neutral-100 rounded-lg animate-pulse" />
      ) : loads.length === 0 ? (
        <p className="text-sm text-neutral-600">Nothing to treat: every order on an open load is treated or goes under no protocol.</p>
      ) : (
        <>
          <p className="text-sm text-neutral-700">
            {`${pickedCount} picked order${pickedCount === 1 ? '' : 's'} ready for the spray area, on ${loads.length} load${loads.length === 1 ? '' : 's'}.`}
          </p>
          {loads.map((l) => <LoadBlock key={l.load_id} l={l} tz={tz} />)}
        </>
      )}
    </section>
  );
}

export default QuarantineQueue;
